/**
 * Results card: tier badge, score, summary, tier body (markdown), and optional share CTA.
 * Renders only when all items are rated (isComplete).
 */
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Badge } from './ui/badge';
import { Button } from './ui/button';
import type { ScoreData } from '../types';
import type { ResultsGuide } from '../types';
import { getTierByScore } from '../utils/scoreCalculator';
import { SCORE_CONFIG, TIER_PILL_STYLES } from '../constants';

interface ScoreResultsProps {
  score: ScoreData;
  resultsGuide: ResultsGuide | null;
  /** When provided, shows a "Share your results" button at the bottom that calls this (e.g. opens share modal). */
  onShareClick?: () => void;
}

export const ScoreResults: React.FC<ScoreResultsProps> = ({ score, resultsGuide, onShareClick }) => {
  const isComplete = score.rated >= SCORE_CONFIG.perfectScore.rated;
  const tierLevel = getTierByScore(score.total);
  const tier = resultsGuide?.tiers.find((t) => t.level === tierLevel);
  const pillStyles = TIER_PILL_STYLES[tierLevel];

  if (!isComplete) {
    return null;
  }

  return (
    <div className="mb-16" id="results">
      <div className="p-5 bg-white overflow-visible border-0 rounded-xl">
        <div className="prose max-w-none [&>:first-child]:mt-0 [&>:last-child]:mb-0 text-center mb-6">
          <h2 className="text-text-primary mb-2">
            {tier ? tier.headline : `Your design system is Tier ${tierLevel}`}
          </h2>
        </div>

        {tier && (
          <div className="rounded-xl border border-border overflow-hidden mb-6">
            <div className="bg-slate-50 px-5 pt-5 pb-4">
              <div className="flex flex-col items-center gap-2 mb-3">
                <span
                  className="text-xs font-semibold uppercase tracking-wider text-text-secondary"
                  aria-label={`Score: ${score.total} out of ${score.maxScore} points`}
                >
                  {score.total}/{score.maxScore} points
                </span>
                <Badge
                  variant="outline"
                  className={`${pillStyles.bgColor} ${pillStyles.color} border-0 text-[16px] font-semibold px-4 py-1.5`}
                >
                  {tier.name} {tier.emoji}
                </Badge>
              </div>
              <p className="text-center text-text-secondary font-medium mb-4">
                {tier.summary}
              </p>
              <div className="text-left">
                <p className="font-semibold text-[#111827] mb-1 mt-0">What this means</p>
                <p className="text-[#4b5563] leading-[1.625] mt-0 mb-0">{tier.whatThisMeans}</p>
              </div>
            </div>
            {tier.body && (
              <>
                <div className="border-t border-border" aria-hidden />
                <div className="bg-white px-5 pt-7 pb-5 results-tier-prose prose prose-slate max-w-none [&>:first-child]:mt-0 [&>:last-child]:mb-0">
                  <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ h3: 'h2' }}>{tier.body}</ReactMarkdown>
                </div>
              </>
            )}
            {onShareClick && (
              <div className="border-t border-border bg-white px-5 pt-4 pb-5">
                <Button
                  variant="outline"
                  size="default"
                  className="w-full"
                  onClick={onShareClick}
                  aria-label="Share your results"
                >
                  Share your results
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
